import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { DollarSign, Users, Wrench, ReceiptText, TrendingUp, ArrowUpRight, CalendarDays } from "lucide-react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";

const money = (n) => "A$" + (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

function RevenueCard({ label, data, accent, testid }) {
  return (
    <div className={`rounded-sm border border-border border-l-4 ${accent} bg-card p-6 h-full relative overflow-hidden transition-colors duration-200`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground flex items-center gap-1.5"><CalendarDays size={14} /> {label}</span>
      </div>
      <p className="text-3xl font-heading font-bold mt-3 tracking-tight">{money(data.revenue)}</p>
      <p className="text-xs text-muted-foreground mt-1">{data.count} sale{data.count !== 1 && "s"} · net of refunds</p>
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub, testid, accent, iconColor }) {
  return (
    <div className={`rounded-sm border border-border border-l-4 ${accent} bg-card p-5 h-full transition-colors duration-200`} data-testid={testid}>
      <div className="flex items-center justify-between"><span className="text-sm text-muted-foreground">{label}</span><Icon size={17} className={iconColor} /></div>
      <p className="text-2xl font-heading font-bold mt-2 tracking-tight">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

export default function Dashboard() {
  const [s, setS] = useState(null);
  const navigate = useNavigate();
  const load = () => api.get("/dashboard/stats").then((r) => setS(r.data)).catch(() => {});
  useEffect(() => { load(); const h = () => load(); window.addEventListener("zeefa:refresh", h); return () => window.removeEventListener("zeefa:refresh", h); }, []);
  if (!s) return <div className="text-muted-foreground">Loading dashboard…</div>;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-heading font-bold tracking-tight">Sales Dashboard</h1>
        <p className="text-muted-foreground mt-1">Revenue by period — net of refunds.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}><RevenueCard label="Today" data={s.today} accent="border-l-[#BE1230]" testid="rev-today" /></motion.div>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.06 }}><RevenueCard label="Last 7 days" data={s.last7} accent="border-l-blue-500" testid="rev-7d" /></motion.div>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}><RevenueCard label="Last 30 days" data={s.last30} accent="border-l-emerald-500" testid="rev-30d" /></motion.div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Users} label="Customers" value={s.total_clients.toLocaleString()} accent="border-l-amber-500" iconColor="text-amber-500" testid="stat-clients" />
        <Stat icon={Wrench} label="Open Cases" value={s.open_cases} sub="open & in progress" accent="border-l-purple-500" iconColor="text-purple-500" testid="stat-cases" />
        <Stat icon={ReceiptText} label="Pending Invoices" value={money(s.pending_total)} sub={`${s.pending_count} awaiting`} accent="border-l-rose-500" iconColor="text-rose-500" testid="stat-pending" />
        <Stat icon={TrendingUp} label="30-day Sales" value={s.last30.count} accent="border-l-cyan-500" iconColor="text-cyan-500" testid="stat-30count" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-sm border border-border bg-card">
          <div className="px-6 py-4 border-b border-border"><h3 className="font-heading font-semibold flex items-center gap-2"><TrendingUp size={17} className="text-primary" /> Recent Sales</h3></div>
          <div className="divide-y divide-border" data-testid="recent-sales-list">
            {s.recent_sales.length === 0 && <p className="px-6 py-8 text-sm text-muted-foreground text-center">No sales yet.</p>}
            {s.recent_sales.map((l) => (
              <div key={l.id} onClick={() => navigate(`/clients/${l.client_id}`)} className="flex items-center gap-3 px-6 py-3 cursor-pointer hover:bg-accent transition-colors">
                <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{l.name}</p><p className="text-xs text-muted-foreground truncate">{l.product}</p></div>
                <span className="font-mono text-sm font-semibold">{money(l.amount)}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-sm border border-border bg-card">
          <div className="flex items-center justify-between px-6 py-4 border-b border-border"><h3 className="font-heading font-semibold flex items-center gap-2"><ReceiptText size={17} className="text-primary" /> Pending Invoices</h3><button onClick={() => navigate("/invoices")} className="text-xs text-primary flex items-center gap-1 hover:underline">View all <ArrowUpRight size={13} /></button></div>
          <div className="divide-y divide-border" data-testid="pending-invoices-list">
            {s.pending_invoices.length === 0 && <p className="px-6 py-8 text-sm text-muted-foreground text-center">No pending invoices.</p>}
            {s.pending_invoices.map((i) => (
              <div key={i.id} onClick={() => i.client_id && navigate(`/clients/${i.client_id}`)} className={`flex items-center gap-3 px-6 py-3 ${i.client_id ? "cursor-pointer hover:bg-accent transition-colors" : ""}`}>
                <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{i.client_name || "—"}</p><p className="text-xs text-muted-foreground font-mono truncate">{i.invoice_no} · due {i.due_date || "—"}</p></div>
                <Badge variant={i.status === "Overdue" ? "destructive" : "outline"} className="rounded-sm">{i.status}</Badge>
                <span className="font-mono text-sm font-semibold">{money(i.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
